import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { Popover } from 'primeng/popover';
import { TOPBAR_MENU, TOPBAR_MENU_ADMIN } from '@gestor-web/@core/menu';
import { OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { Store } from '@ngrx/store';
import { ContextoAcesso, PermissaoService } from '@gestor-web/app/shared/services';
import {
  buildAnalisesThirdLevelSearchItems,
  buildMenuGroupSearchItems,
  buildProfileSearchItems,
  buildSupportSearchItems,
  buildTopbarSearchItemsWithAccess,
  SearchMenuItem,
} from '@gestor-web/app/shared/components/search/search.utils';
import { AppComponent } from '@gestor-web/app/app.component';
import { combineLatest, Subject, debounceTime, takeUntil } from 'rxjs';
import { AppMainComponent } from '../app.main.component';
import { getMenuGroupHref, getMenuItemHref } from '@gestor-web/app/shared/utils/menu-href.utils';

@Component({
    selector: 'app-inline-menu',
    templateUrl: './app.inlinemenu.component.html',
    styleUrls: ['./app.inlinemenu.component.scss'],
    animations: [
        trigger('menu', [
            state('hiddenAnimated', style({
                height: '0px',
                paddingBottom: '0px',
                overflow: 'hidden',
            })),
            state('visibleAnimated', style({
                height: '*',
                overflow: 'visible',
            })),
            state('visible', style({
                opacity: 1,
                'z-index': 100,
            })),
            state('hidden', style({
                opacity: 0,
                'z-index': '*',
            })),
            transition('visibleAnimated => hiddenAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('hiddenAnimated => visibleAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
            transition('visible => hidden', animate('.1s linear')),
            transition('hidden => visible', [
                style({ transform: 'scaleY(0.8)' }),
                animate('.12s cubic-bezier(0, 0, 0.2, 1)'),
            ]),
        ]),
    ],
    standalone: false
})
export class AppInlineMenuComponent implements OnInit, OnDestroy {
  @Input() key = 'inline-menu';

  @Input() style: any;

  @Input() styleClass: string;

  @Input() isCompact = false;

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('searchShell') searchShell?: ElementRef<HTMLElement>;
  @ViewChildren('searchResultItem') searchResultItems?: QueryList<ElementRef<HTMLElement>>;

  active: boolean;

  searchDialogVisible = false;
  searchTerm = '';
  debouncedSearchTerm = '';
  highlightedSearchIndex = -1;

  currentOperadora: any;
  isCurrentUserAdmin = false;

  hoveredGroup: string | null = null;
  isFlyoutFixedMode = false;
  fixedFlyoutGroup: string | null = null;
  activeGroupKey: string | null = null;

  selectedLockedSearchItem: SearchMenuItem | null = null;
  private activeSearchLockedItemKey: string | null = null;

  extraSearchItems: SearchMenuItem[] = [];
  topbarSearchItems: SearchMenuItem[] = [];
  supportSearchItems: SearchMenuItem[] = [];
  profileSearchItems: SearchMenuItem[] = [];

  operadora$ = this.store.select(OperadorasSelectors.selectWorkspaceOperadora);

  private readonly searchTerm$ = new Subject<string>();
  private readonly ngDestroyed$ = new Subject<void>();

  constructor(
    public appMain: AppMainComponent,
    public app: AppComponent,
    private store: Store,
    private router: Router,
    private permissaoService: PermissaoService,
  ) {}

  ngOnInit(): void {
    this.setupSearchDebounce();
    this.setupMenuAccessState();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.searchDialogVisible) {
      return;
    }

    const target = event.target as Node | null;
    const searchShellElement = this.searchShell?.nativeElement;

    if (!target || !searchShellElement) {
      return;
    }

    const clickedInsideSearch = searchShellElement.contains(target);

    if (!clickedInsideSearch) {
      this.closeSearchDialog();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent): void {
    const isCtrlOrMetaK = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k';
    const isEscape = event.key === 'Escape';

    if (isCtrlOrMetaK) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.searchDialogVisible) {
        this.openSearchDialog();
      } else {
        this.focusSearchInput();
      }

      return;
    }

    if (isEscape && this.searchDialogVisible) {
      event.preventDefault();
      event.stopPropagation();
      this.closeSearchDialog();
      return;
    }

    if (!this.searchDialogVisible || !this.shouldShowSearchResults) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      this.moveSearchHighlight(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      this.moveSearchHighlight(-1);
      return;
    }

    if (event.key === 'Enter' && this.highlightedSearchIndex >= 0) {
      const item = this.filteredSearchItems[this.highlightedSearchIndex];

      if (!item) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.onSearchResultClick(event, item);
    }
  }

  get hasSearchTerm(): boolean {
    return !!this.searchTerm?.trim();
  }

  get searchDialogStyle(): Record<string, string> {
    return {
      width: '52rem',
      maxWidth: 'calc(100vw - 2rem)',
    };
  }

  get searchDialogContentStyle(): Record<string, string> {
    return {
      padding: '0',
      overflow: 'hidden',
      background: 'transparent',
    };
  }

  setFixedGroup(groupKey: string | null): void {
    this.isFlyoutFixedMode = !!groupKey;
    this.fixedFlyoutGroup = groupKey;
    this.activeGroupKey = groupKey;
  }

  onMouseEnterGroup(groupKey: string): void {
    this.hoveredGroup = groupKey;
    this.activeGroupKey = groupKey;
  }

  onMouseLeaveGroup(): void {
    this.hoveredGroup = null;
    this.fixedFlyoutGroup = null;
    this.isFlyoutFixedMode = false;
    this.activeGroupKey = null;
  }

  onSearchTermInput(value: string): void {
    this.searchTerm = value;
    this.highlightedSearchIndex = -1;

    const trimmedValue = value.trim();

    if (!trimmedValue) {
      this.debouncedSearchTerm = '';
      this.searchTerm$.next('');
      return;
    }

    this.searchTerm$.next(trimmedValue);
  }

  private resetMenuState(): void {
    this.appMain.inlineMenuClick = true;
    this.hoveredGroup = null;
    this.fixedFlyoutGroup = null;
    this.activeGroupKey = null;
    this.isFlyoutFixedMode = false;
  }

  private navigateTo(commands?: any[], closeSearch = false, url?: string, target?: string): void {
    if (url) {
      this.resetMenuState();

      if (closeSearch) {
        this.closeSearchDialog();
      }

      window.open(url, target || '_blank');
      return;
    }

    if (!commands?.length) {
      return;
    }

    const urlTree = this.router.createUrlTree(commands);

    this.resetMenuState();

    void this.router.navigateByUrl(urlTree).then(navigated => {
      if (navigated && closeSearch) {
        this.closeSearchDialog();
      }
    });
  }

  onClick(event: Event, key?: string): void {
    this.appMain.onInlineMenuClick(event, this.key);

    if (key === 'dashboard') {
      this.fixedFlyoutGroup = null;
      this.isFlyoutFixedMode = false;
    }

    event.preventDefault();
  }

  openSearchDialog(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.appMain.inlineMenuClick = true;
    this.hoveredGroup = null;
    this.fixedFlyoutGroup = null;
    this.activeGroupKey = 'pesquisar';
    this.searchTerm = '';
    this.debouncedSearchTerm = '';
    this.highlightedSearchIndex = -1;
    this.selectedLockedSearchItem = null;
    this.activeSearchLockedItemKey = null;
    this.searchTerm$.next('');
    this.searchDialogVisible = true;

    setTimeout(() => {
      this.focusSearchInput();
    }, 0);
  }

  onSearchDialogShow(): void {
    setTimeout(() => {
      this.focusSearchInput();
    }, 0);
  }

  closeSearchDialog(): void {
    this.searchDialogVisible = false;
    this.searchTerm = '';
    this.debouncedSearchTerm = '';
    this.highlightedSearchIndex = -1;
    this.activeGroupKey = null;
    this.hoveredGroup = null;
    this.fixedFlyoutGroup = null;
    this.isFlyoutFixedMode = false;
    this.selectedLockedSearchItem = null;
    this.activeSearchLockedItemKey = null;
    this.searchTerm$.next('');
  }

  isSearchItemLocked(item: SearchMenuItem): boolean {
    return !!item.locked && !this.isCurrentUserAdmin;
  }

  canAccessSearchItem(item: SearchMenuItem): boolean {
    if (this.isCurrentUserAdmin) {
      return !!item.route?.length || !!item.url;
    }

    return !this.isSearchItemLocked(item) && (!!item.route?.length || !!item.url);
  }

  private getSearchItemLockButtonFromEvent(event: Event | KeyboardEvent): HTMLElement | null {
    const currentTarget = event.currentTarget as HTMLElement | null;

    if (!currentTarget) {
      return null;
    }

    return currentTarget.querySelector('.raw-search-lock-button');
  }

  private getSearchItemKey(item: SearchMenuItem): string {
    return [item.label, item.groupLabel, item.parentLabel, item.url, item.route?.join('/')].filter(Boolean).join('|');
  }

  private toggleSearchLockedOverlay(overlay: Popover, item: SearchMenuItem, target: HTMLElement): void {
    const itemKey = this.getSearchItemKey(item);
    const isSameItemOpen = overlay.overlayVisible && this.activeSearchLockedItemKey === itemKey;

    if (isSameItemOpen) {
      overlay.hide();
      return;
    }

    this.selectedLockedSearchItem = item;
    this.activeSearchLockedItemKey = itemKey;
    overlay.show(null, target);
  }

  onSearchLockedOverlayHide(): void {
    this.activeSearchLockedItemKey = null;
  }

  onSearchResultClick(event: Event | KeyboardEvent, item: SearchMenuItem, overlay?: Popover): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isSearchItemLocked(item)) {
      if (overlay) {
        const lockButton = this.getSearchItemLockButtonFromEvent(event);

        if (lockButton) {
          this.toggleSearchLockedOverlay(overlay, item, lockButton);
        }
      }
      return;
    }

    if (!this.canAccessSearchItem(item)) {
      return;
    }

    this.navigateTo(item.route, true, item.url, item.target);
  }

  onSearchLockClick(event: Event, item: SearchMenuItem, overlay: Popover): void {
    event.preventDefault();
    event.stopPropagation();

    const lockButton = event.currentTarget as HTMLElement | null;

    if (!lockButton) {
      return;
    }

    this.toggleSearchLockedOverlay(overlay, item, lockButton);
  }

  trackBySearchItem(_index: number, item: SearchMenuItem): string {
    return `${item.groupKey}|${item.parentLabel || ''}|${item.label}|${item.route?.join('/') || item.url || ''}`;
  }

  onGroupActionClick(event: Event, group: MenuGroup, operadora: any): void {
    event.preventDefault();
    event.stopPropagation();

    this.appMain.inlineMenuClick = true;
    this.activeGroupKey = group.key;

    if (group.key === 'pesquisar') {
      this.openSearchDialog();
      return;
    }

    if (group.items.length === 1 && group.items[0].route) {
      const route = group.items[0].route(operadora);
      this.navigateTo(route);
      return;
    }

    this.setFixedGroup(group.key);
    this.onMouseEnterGroup(group.key);
  }

  onMenuItemClick(event: Event, item: MenuItem, operadora: any): void {
    event.preventDefault();
    event.stopPropagation();

    if (item.locked && !this.isCurrentUserAdmin) {
      return;
    }

    if (!item.route) {
      return;
    }

    const route = item.route(operadora);
    this.navigateTo(route);
  }

  onLockedItemClick(event: Event, item: MenuItem, operadora: any, lockedPanel: Popover): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isCurrentUserAdmin) {
      if (!item.route) {
        return;
      }

      const route = item.route(operadora);
      this.navigateTo(route);
      return;
    }

    lockedPanel.toggle(event);
  }

  getItemHref(item: MenuItem, operadora: any): string {
    return getMenuItemHref(item, operadora, this.router);
  }

  getGroupHref(group: MenuGroup, operadora: any): string {
    return getMenuGroupHref(group, operadora, this.router);
  }

  get searchableMenuItems(): SearchMenuItem[] {
    if (!this.currentOperadora?.cd_registro_ans) {
      return [];
    }

    const baseItems = buildMenuGroupSearchItems({
      menuGroups: this.menuGroups,
      operadora: this.currentOperadora,
    });

    return [
      ...baseItems,
      ...this.topbarSearchItems,
      ...this.supportSearchItems,
      ...this.profileSearchItems,
      ...this.extraSearchItems,
    ];
  }

  get filteredSearchItems(): SearchMenuItem[] {
    const normalizedTerm = this.normalizeText(this.debouncedSearchTerm);

    if (!normalizedTerm) {
      return [];
    }

    const tokens = normalizedTerm.split(' ').filter(Boolean);

    return this.searchableMenuItems
      .map(item => {
        const normalizedLabel = this.normalizeText(item.label);
        const allTokensMatch = tokens.every(token => normalizedLabel.includes(token));

        if (!allTokensMatch) {
          return { item, score: 0 };
        }

        let score = 0;

        if (normalizedLabel.startsWith(normalizedTerm)) score += 120;
        if (normalizedLabel.includes(normalizedTerm)) score += 80;

        for (const token of tokens) {
          if (normalizedLabel.startsWith(token)) {
            score += 20;
          } else if (normalizedLabel.includes(token)) {
            score += 12;
          }
        }

        return { item, score };
      })
      .filter(result => result.score > 0)
      .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label))
      .map(result => result.item)
      .slice(0, 20);
  }

  get isTooltipDisabled(): boolean {
    return !(this.appMain.isSlim() && !this.appMain.isMobile());
  }

  get tabIndex(): string | null {
    return !this.appMain.inlineMenuActive ? '-1' : null;
  }

  get shouldShowSearchResults(): boolean {
    return !!this.debouncedSearchTerm?.trim();
  }

  isHorizontalActive(): boolean {
    return this.appMain.isHorizontal() && !this.appMain.isMobile();
  }

  private moveSearchHighlight(direction: 1 | -1): void {
    const items = this.filteredSearchItems;

    if (!items.length) {
      this.highlightedSearchIndex = -1;
      return;
    }

    if (this.highlightedSearchIndex === -1) {
      this.highlightedSearchIndex = direction === 1 ? 0 : items.length - 1;
      this.scrollHighlightedSearchItemIntoView();
      return;
    }

    const nextIndex = this.highlightedSearchIndex + direction;

    if (nextIndex < 0) {
      this.highlightedSearchIndex = items.length - 1;
      this.scrollHighlightedSearchItemIntoView();
      return;
    }

    if (nextIndex >= items.length) {
      this.highlightedSearchIndex = 0;
      this.scrollHighlightedSearchItemIntoView();
      return;
    }

    this.highlightedSearchIndex = nextIndex;
    this.scrollHighlightedSearchItemIntoView();
  }

  private syncHighlightedIndexWithResults(): void {
    const items = this.filteredSearchItems;

    if (!items.length) {
      this.highlightedSearchIndex = -1;
      return;
    }

    if (this.highlightedSearchIndex >= items.length) {
      this.highlightedSearchIndex = -1;
      return;
    }

    this.scrollHighlightedSearchItemIntoView();
  }

  private scrollHighlightedSearchItemIntoView(): void {
    setTimeout(() => {
      const index = this.highlightedSearchIndex;

      if (index < 0) {
        return;
      }

      const searchItems = this.searchResultItems?.toArray() ?? [];
      const activeItem = searchItems[index]?.nativeElement;

      if (!activeItem) {
        return;
      }

      activeItem.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth',
      });
    });
  }

  private setupSearchDebounce(): void {
    this.searchTerm$.pipe(debounceTime(400), takeUntil(this.ngDestroyed$)).subscribe(term => {
      this.debouncedSearchTerm = term;
      this.syncHighlightedIndexWithResults();
    });
  }

  private setupMenuAccessState(): void {
    combineLatest([
      this.store.select(OperadorasSelectors.selectWorkspaceOperadora),
      this.permissaoService.getContextoAcesso(),
    ])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([operadora, contexto]) => {
        if (!operadora?.cd_registro_ans) return;
        this.currentOperadora = operadora;

        const isAdmin = contexto.isAdmin;
        this.isCurrentUserAdmin = isAdmin;

        // O acesso é decidido pela lista de funcionalidades retornada pelo back-end.
        this.applyMenuAccessRules(contexto);

        const analisesGroup = this.menuGroups.find(group => group.key === 'analises');

        this.extraSearchItems = buildAnalisesThirdLevelSearchItems({
          operadoraCdRegistroAns: this.currentOperadora.cd_registro_ans,
          contexto,
          analisesGroup,
          permissaoService: this.permissaoService,
        });

        this.topbarSearchItems = [
          ...buildTopbarSearchItemsWithAccess({
            menu: TOPBAR_MENU,
            contexto,
            permissaoService: this.permissaoService,
          }),
          ...buildTopbarSearchItemsWithAccess({
            menu: TOPBAR_MENU_ADMIN,
            contexto,
            adminOnly: true,
            permissaoService: this.permissaoService,
          }),
        ];

        this.supportSearchItems = buildSupportSearchItems();
        this.profileSearchItems = buildProfileSearchItems();
      });
  }

  private applyMenuAccessRules(contexto: ContextoAcesso): void {
    const isAdmin = contexto.isAdmin;

    this.menuGroups.forEach(group => {
      group.items.forEach((item: MenuItem) => {
        item.locked = false;
        item.lockedModules = [];
        item.isAdmin = isAdmin;

        if (item.label === 'Indicadores NIPs') {
          item.invisible = !isAdmin;
          return;
        }

        if (item.label === 'Correção de guias e itens') {
          item.invisible = !this.permissaoService.resolverAcesso(item.featureCode, contexto).acessivel;
          return;
        }

        if (!item.featureCode) {
          return;
        }

        // Itens que a operadora não contratou somem do menu; o administrador continua
        // enxergando o cadeado (e navegando) para saber o que ela não possui.
        const acesso = this.permissaoService.resolverAcesso(item.featureCode, contexto);
        item.invisible = !acesso.visivel;
        item.locked = acesso.locked;
        item.lockedModules = acesso.lockedModules;
      });

      // Grupo sem nenhum item visível some por inteiro: deixá-lo na barra abriria um
      // flyout vazio, anunciando uma seção que a operadora não possui.
      group.invisible = group.items.every(item => item.invisible);
    });
  }

  private focusSearchInput(): void {
    this.searchInput?.nativeElement?.focus();
    this.searchInput?.nativeElement?.select();
  }

  private normalizeText(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }

  menuGroups: MenuGroup[] = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: 'bl bl-dashboard',
      items: [
        {
          label: 'Dashboard',
          icon: 'bl bl-dashboard',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}`],
          featureCode: 'DASHBOARD',
        },
      ],
    },
    {
      key: 'pesquisar',
      label: 'Pesquisar',
      icon: 'pi pi-search',
      items: [
        {
          label: 'Pesquisar',
          icon: 'pi pi-search',
        },
      ],
    },
    {
      key: 'criticas',
      label: 'Críticas',
      icon: 'bl bl-criticas',
      items: [
        {
          label: 'Painel de críticas',
          icon: 'bl bl-criticas-guias-itens',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/criticas/painel-criticas`],
          featureCode: 'GUIAS_TISS.CRITICAS_GUIAS_ITENS',
        },
        {
          label: 'Resumo do histórico das críticas',
          icon: 'bl bl-resumo-historico-criticas',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/criticas/resumo-historico-criticas`],
          featureCode: 'GUIAS_TISS.RESUMO_HISTORICO_CRITICAS',
        },
        {
          label: 'Retornos da ANS',
          icon: 'bl bl-retornos-ans',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/criticas/retornos-ans`],
          featureCode: 'GUIAS_TISS.RETORNOS_ANS',
        },
        {
          label: 'Críticas por operadora intermediária',
          icon: 'bl bl-criticas-operadora-intermediaria',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/analises/conformidade/criticas-operadora-intermediaria`,],
          featureCode: 'CONFORMIDADE.CRITICAS_OPERADORA_INTERMEDIARIA',
        },
      ],
    },

    {
      key: 'guias-tiss',
      label: 'Guias TISS',
      icon: 'bl bl-guias-tiss',
      items: [
        {
          label: 'Resumo de guias',
          icon: 'bl bl-resumo-guias',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/guias-tiss/resumo-guias`],
          featureCode: 'GUIAS_TISS.RESUMO_GUIAS',
        },
        {
          label: 'Resumo de itens das guias',
          icon: 'bl bl-resumo-itens-das-guias',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/guias-tiss/resumo-itens-guias`],
          featureCode: 'GUIAS_TISS.RESUMO_ITENS_GUIAS',
        },
        {
          label: 'Listagem de guias',
          icon: 'bl bl-listagem-guias',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/guias-tiss/listagem-guias`],
          featureCode: 'GUIAS_TISS.LISTAGEM_GUIAS',
        },
        {
          label: 'Lançamentos e valores das guias',
          icon: 'bl bl-lancamentos-valores-guias',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/guias-tiss/lancamentos-guias`],
          featureCode: 'GUIAS_TISS.LANCAMENTOS_VALORES_GUIAS',
        },
        {
          label: 'Correção de guias e itens',
          icon: 'bl bl-correcao-guias',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/guias-tiss/correcao-guias`],
          invisible: true,
          featureCode: 'GUIAS_TISS.CORRECAO_GUIAS_ITENS',
        },
      ],
    },

    {
      key: 'outras-remuneracoes',
      label: 'Outras remunerações',
      icon: 'bl bl-outras-remuneracoes',
      items: [
        {
          label: 'Valor preestabelecido',
          icon: 'bl bl-valor-preestabelecido',
          route: (operadora: any) => [
            `/operadora/${operadora.cd_registro_ans}/outras-remuneracoes/valor-preestabelecido`,
          ],
          featureCode: 'OUTRAS_REMUNERACOES.VALOR_PREESTABELECIDO',
        },
        {
          label: 'Fornecimento direto',
          icon: 'bl bl-fornecimento-direto',
          route: (operadora: any) => [
            `/operadora/${operadora.cd_registro_ans}/outras-remuneracoes/fornecimento-direto`,
          ],
          featureCode: 'OUTRAS_REMUNERACOES.FORNECIMENTO_DIRETO',
        },
        {
          label: 'Despesas assistenciais',
          icon: 'bl bl-despesas-assistenciais',
          route: (operadora: any) => [
            `/operadora/${operadora.cd_registro_ans}/outras-remuneracoes/despesas-assistenciais`,
          ],
          featureCode: 'OUTRAS_REMUNERACOES.DESPESAS_ASSISTENCIAIS',
        },
      ],
    },

    {
      key: 'nips',
      label: 'NIPs',
      icon: 'bl bl-nips',
      items: [
        {
          label: 'Listagem de NIPs',
          icon: 'bl bl-listagem-nips',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/nips`],
          featureCode: 'NIPS.LISTAGEM_NIPS',
        },
        {
          // Visível somente para administradores (ver applyMenuAccessRules). Sem featureCode:
          // a visibilidade não depende da lista de funcionalidades do back-end.
          label: 'Indicadores NIPs',
          icon: 'bl bl-indicadores-nips',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/nips/indicadores`],
        },
      ],
    },

    {
      key: 'analises',
      label: 'Análises',
      icon: 'bl bl-analises',
      items: [
        {
          label: 'Conformidade',
          icon: 'bl bl-conformidade',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/analises/conformidade`],
          featureCode: 'CONFORMIDADE',
        },
        {
          label: 'Análise de rede',
          icon: 'bl bl-analise-de-rede',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/analises/analise-de-rede`],
          featureCode: 'ANALISE_REDE',
        },
        {
          label: 'Incorporação de dados',
          icon: 'bl bl-incorporacao-de-dados',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/analises/incorporacao-de-dados`],
          featureCode: 'INCORPORACAO_DADOS',
        },
        {
          label: 'Indicadores IDSS',
          icon: 'bl bl-indicadores-idss',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/analises/indicadores-idss`],
          featureCode: 'INDICADORES_IDSS',
        },
      ],
    },

    {
      key: 'simulador',
      label: 'Simulador',
      icon: 'bl bl-simulador',
      items: [
        {
          label: 'Simulador/Projeções',
          icon: 'bl bl-simulador-projecoes',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/simulador-idss`],
          featureCode: 'SIMULADOR',
        },
        {
          label: 'Detalhes da apuração',
          icon: 'pi pi-list',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/simulador-idss/detalhes-apuracao`],
          featureCode: 'SIMULADOR.VISUALIZAR_SIMULADO',
        },
        //{
        //label: 'Simulador MRA',
        //icon: 'bl bl-simulador-projecoes',
        //route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/simulador-mra`],
        //featureCode: 'simulador',
        //},
        {
          label: 'Comparativo TISS e DIOPS',
          icon: 'bl bl-comparativo-tiss-diops',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/guias-tiss/comparativo-tiss-diops`],
          featureCode: 'SIMULADOR.COMPARATIVO_TISS_DIOPS',
        },
      ],
    },

    {
      key: 'redimensionamento-rede',
      label: 'Redimensiona',
      icon: 'bl bl-redimensiona',
      items: [
        {
          label: 'Redimensionamento hospitalar (RN 585)',
          icon: 'bl bl-rede-hospitalar-rn585',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/redimensionamento-rede`],
        },
        {
          label: 'Redimensionamento não hospitalar (RN 567)',
          icon: 'bl bl-rede-nao-hospitalar-rn567',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/redimensionamento-nao-hospitalar`],
        },
      ],
    },

    {
      key: 'arquivos',
      label: 'Arquivos',
      icon: 'bl bl-arquivos',
      items: [
        {
          label: 'Postar',
          icon: 'bl bl-postar',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/arquivos/postar`],
          featureCode: 'ARQUIVOS.POSTAR_ARQUIVOS',
        },
        {
          label: 'Arquivos da operadora',
          icon: 'bl bl-arquivos-operadora',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/arquivos/arquivos-postados`],
          featureCode: 'ARQUIVOS.ARQUIVOS_OPERADORA',
        },
        {
          label: 'Lotes XTE',
          icon: 'bl bl-lotes-xte',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/arquivos/lotes-xte`],
          featureCode: 'ARQUIVOS.LOTES_XTE',
        },
        {
          label: 'Baixar',
          icon: 'bl bl-baixar',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/arquivos/baixar`],
          featureCode: 'ARQUIVOS.BAIXAR_ARQUIVOS',
        },
        {
          label: 'Situação',
          icon: 'bl bl-situacao',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/arquivos/situacao`],
          featureCode: 'ARQUIVOS.SITUACAO_ARQUIVOS',
        },
        {
          label: 'Apresentações',
          icon: 'bl bl-apresentacoes',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/arquivos/apresentacoes`],
          featureCode: 'ARQUIVOS.APRESENTACOES',
        },
      ],
    },

    {
      key: 'minha-operadora',
      label: 'Minha operadora',
      icon: 'bl bl-minha-operadora',
      items: [
        {
          label: 'Planos de saúde',
          icon: 'bl bl-planos-saude',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/minha-operadora/planos-de-saude`],
          featureCode: 'MINHA_OPS.PLANOS_SAUDE',
        },
        {
          label: 'Pacotes e procedimentos próprios',
          icon: 'bl bl-pacotes-procedimentos-proprios',
          route: (operadora: any) => [
            `/operadora/${operadora.cd_registro_ans}/minha-operadora/pacotes-e-procedimentos`,
          ],
          featureCode: 'MINHA_OPS.PACOTES_PROCEDIMENTOS_PROPRIOS',
        },
        {
          label: 'Resumo do CNX',
          icon: 'bl bl-resumo-cnx',
          route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/minha-operadora/resumo-cnx`],
          featureCode: 'MINHA_OPS.RESUMO_CNX',
        },
      ],
    },

    // {
    //   key: 'poc',
    //   label: 'POC',
    //   visible: false,
    //   items: [
    //     {
    //       label: 'AG Grid',
    //       route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/poc/ag-grid`],
    //     },
    //     {
    //       label: 'ORDS API Test',
    //       route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/poc/ords-api-test`],
    //     },
    //     {
    //       label: 'APEX in iframe',
    //       route: (operadora: any) => [`/operadora/${operadora.cd_registro_ans}/poc/apex-iframe`],
    //     },
    //   ],
    // },

    // {
    //   key: 'ajuda',
    //   label: 'AJUDA',
    //   visible: false,
    //   items: [
    //     {
    //       label: 'Documentação arquitetura',
    //       url: '/assets/doc/index.html',
    //       target: '_blank',
    //     },
    //     {
    //       label: 'Tema',
    //       url: 'https://primefaces.org/ultima-ng/#/documentation',
    //       target: '_blank',
    //     },
    //   ],
    // },
  ];
}

type MenuGroup = {
  key: string;
  label: string;
  icon: string;
  items: MenuItem[];
  invisible?: boolean;
};

type MenuItem = {
  label: string;
  icon: string;
  route?: (operadora: any) => any[];
  featureCode?: string;
  invisible?: boolean;
  locked?: boolean;
  lockedModules?: string[];
  isAdmin?: boolean;
};
